import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { SingleTextForm, InputMatches } from "../shared/SingleTextForm";
import { OUTCOMES } from "../../../constants";
import { default as NumberFormat } from "react-number-format";
import { estateApi } from "../../../apis";
import { Outcomes } from "../shared/PropertyOwnershipScreen";
import { AssetContext } from "../../../AssetContextWrapper";
import { AssetRealEstate } from "../../../models/AssetDetail";
interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumberFormatCustom = React.forwardRef<NumberFormat<any>, CustomProps>(
  function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        decimalSeparator="."
        allowNegative={false}
        decimalScale={0}
        fixedDecimalScale={false}
        isNumericString={true}
      />
    );
  }
);

export const PropertyMortgageScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { assetsLoaded } = useContext(AssetContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const primaryRealEstate = activeEstate?.primaryRealEstate;
  const reAsset = primaryRealEstate?.detail as AssetRealEstate;

  // Only allow for positive (including 0) numbers.
  const inputMatches: InputMatches = {
    regexp: new RegExp("^[0-9]{1,3}(?:,?[0-9]{3})*$"),
    failMessage:
      "Remaining mortgage balance must be a positive non-decimal currency value.",
  };

  useEffect(() => {
    if (assetsLoaded && !activeEstate?.primaryRealEstate) {
      currentScreen &&
        flow?.navigateToNext(currentScreen, Outcomes.skip, navigate, {
          replace: true,
        });
    }
  }, [assetsLoaded]);

  const handleComplete = async (propertyValue: string): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    // Database does not store commas. Change to empty string.
    propertyValue = propertyValue.replace(/,/g, "");

    if (activeEstate?.id && user?.id && activeEstate?.primaryRealEstate?.detail) {
      (
        activeEstate.primaryRealEstate.detail as AssetRealEstate
      ).remaining_mortgage = parseInt(propertyValue, 10) || null;
      try {
        await estateApi.saveAsset(primaryRealEstate,activeEstate.id, user.id);
      } catch (error) {
        throw new Error("could not save data");
      }
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper
      title="What is the remaining balance left on the mortgage?"
      subTitle={`We’ll use this to calculate how much equity ${activeEstate?.deceasedFirstName} had in the home, or in other words how much of the home ${activeEstate?.deceasedFirstName} owned. Once again, this is paramount in determining what kind of Probate the estate requires.`}
    >
      <>
        <SingleTextForm
          initText={reAsset?.remaining_mortgage?.toString() || undefined}
          inputMatches={inputMatches}
          errorText="Please enter estimated remaining mortgage balance or let us know if you're not sure"
          textLabel="Remaining mortgage balance"
          onComplete={handleComplete}
          onBack={() => navigate(-1)}
          inputComponent={NumberFormatCustom}
          prefix="$"
          noneTextOption="I’m not sure"
        />
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
